import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

/**
 * One shared STOMP/SockJS connection per browser tab.
 * Topics multiplex here so play pages do not open 6–7 sockets each.
 */

let sharedClient = null;
let refCount = 0;
/** @type {Set<() => void>} */
const attachOnConnect = new Set();
/** @type {Set<(status: string) => void>} */
const statusListeners = new Set();
let lastStatus = 'disconnected';

function emitStatus(status) {
  lastStatus = status;
  statusListeners.forEach((fn) => {
    try {
      fn(status);
    } catch {
      /* ignore */
    }
  });
}

function readAuthHeaders() {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function ensureClient() {
  if (sharedClient) return sharedClient;

  sharedClient = new Client({
    webSocketFactory: () => new SockJS('/ws/chess'),
    reconnectDelay: 3000,
    heartbeatIncoming: 10000,
    heartbeatOutgoing: 10000,
    beforeConnect: () => {
      sharedClient.connectHeaders = readAuthHeaders();
      emitStatus('connecting');
    },
    onConnect: () => {
      emitStatus('connected');
      attachOnConnect.forEach((fn) => {
        try {
          fn();
        } catch {
          /* ignore */
        }
      });
    },
    onDisconnect: () => emitStatus('disconnected'),
    onStompError: () => emitStatus('disconnected'),
    onWebSocketClose: () => emitStatus('disconnected'),
  });

  return sharedClient;
}

function retainClient() {
  refCount += 1;
  const client = ensureClient();
  if (!client.active) {
    emitStatus('connecting');
    client.activate();
  }
  return client;
}

function releaseClient() {
  refCount = Math.max(0, refCount - 1);
  if (refCount > 0 || !sharedClient) return;
  const client = sharedClient;
  sharedClient = null;
  emitStatus('disconnected');
  try {
    client.deactivate();
  } catch {
    /* ignore */
  }
}

/**
 * Subscribe to a STOMP destination on the shared client.
 * @returns unsubscribe function
 */
function subscribeTopic(destination, onMessage, onStatus) {
  if (onStatus) {
    statusListeners.add(onStatus);
    onStatus(refCount === 0 && lastStatus === 'disconnected' ? 'connecting' : lastStatus);
  }

  const client = retainClient();
  let subscription = null;
  let cancelled = false;

  const attach = () => {
    if (cancelled || !client.connected) return;
    try {
      subscription?.unsubscribe();
    } catch {
      /* ignore */
    }
    try {
      subscription = client.subscribe(destination, (msg) => {
        try {
          onMessage(JSON.parse(msg.body));
        } catch {
          /* ignore */
        }
      });
    } catch {
      /* ignore */
    }
  };

  attachOnConnect.add(attach);
  if (client.connected) {
    attach();
  }

  return () => {
    cancelled = true;
    attachOnConnect.delete(attach);
    if (onStatus) statusListeners.delete(onStatus);
    try {
      subscription?.unsubscribe();
    } catch {
      /* ignore */
    }
    releaseClient();
  };
}

/**
 * Subscribe to live chess game updates over STOMP/SockJS.
 * @param {number|string} gameId
 * @param {(game: object) => void} onGame
 * @param {(status: 'connecting'|'connected'|'disconnected') => void} [onStatus]
 * @returns unsubscribe function
 */
export function subscribeChessGame(gameId, onGame, onStatus) {
  return subscribeTopic(`/topic/chess/game/${gameId}`, onGame, onStatus);
}

/**
 * Live chat messages for a game.
 */
export function subscribeChessGameChat(gameId, onMessage, onStatus) {
  return subscribeTopic(`/topic/chess/game/${gameId}/chat`, onMessage, onStatus);
}

/**
 * Shared analysis overlays for a game (eval / best move).
 */
export function subscribeChessGameAnalysis(gameId, onAnalysis, onStatus) {
  return subscribeTopic(`/topic/chess/game/${gameId}/analysis`, onAnalysis, onStatus);
}

/**
 * @param {(payload: object) => void} onGame
 * @param {(status: 'connecting'|'connected'|'disconnected') => void} [onStatus]
 */
export function subscribeLiveLobby(onGame, onStatus) {
  return subscribeTopic('/topic/chess/live', onGame, onStatus);
}

/**
 * Incoming challenges for a specific user.
 */
export function subscribeIncomingChallenges(userId, onChallenge, onStatus) {
  if (userId == null) {
    return () => {};
  }
  return subscribeTopic(`/topic/chess/challenges/${userId}`, onChallenge, onStatus);
}

/** Browser event so nav badges refresh after accept/decline without waiting for poll. */
export const PLAYING_CHALLENGES_CHANGED = 'playing:challenges-changed';

export function notifyPlayingChallengesChanged() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(PLAYING_CHALLENGES_CHANGED));
}

/**
 * Open seek lobby updates.
 */
export function subscribeChessSeeks(onSeek, onStatus, userId) {
  const unsubs = [subscribeTopic('/topic/chess/seeks', onSeek, onStatus)];
  if (userId != null) {
    unsubs.push(subscribeTopic(`/topic/chess/seeks/${userId}`, onSeek, undefined));
  }
  return () => {
    unsubs.forEach((u) => {
      try {
        u();
      } catch {
        /* ignore */
      }
    });
  };
}
